import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { sceneAPI, coachAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import { ScenePreview } from './SceneDesignerPage';
import CatalogCard from '../../../../shared/components/CatalogCard';
import './scene_designer.css';

// CRA inlines `process.env.PUBLIC_URL` at build time from the `homepage`
// field in package.json (here: `/training`). All `window.open` calls
// that target React routes MUST prefix this — a bare `/session/...`
// path lands at the nginx root and 404s. Using an empty fallback keeps
// dev mode (no homepage) working.
const APP_BASE = process.env.PUBLIC_URL || '';

// Resolve a scene's catalog entry by matching the scene_type against the
// per-domain scene-type catalog.  Handles three cases in priority order:
//
//   1. Exact match        — "play" → catalog id "play"
//   2. Domain-prefix strip — "football_play" → strip "football_" → "play"
//   3. Suffix match       — any remaining "X_Y" where catalog has id "Y"
//
// Case 2 handles legacy seeded scenes created before the canonical type
// naming convention (canonical = generic id, no domain prefix).  Case 3
// is a belt-and-suspenders catch for other prefix patterns.
function resolveSceneTypeEntry(sceneType, domainId, sceneTypes) {
  const catalog = sceneTypes || [];
  // 1. Exact
  let entry = catalog.find((t) => t.id === sceneType);
  if (entry) return entry;
  // 2. Strip domain prefix  e.g. "football_play" → "play"
  if (domainId && sceneType && sceneType.startsWith(domainId + '_')) {
    const stripped = sceneType.slice(domainId.length + 1);
    entry = catalog.find((t) => t.id === stripped);
    if (entry) return entry;
  }
  // 3. Suffix match  e.g. "football_play" ends with "_play"
  if (sceneType) {
    entry = catalog.find((t) => t.id && sceneType.endsWith('_' + t.id));
    if (entry) return entry;
  }
  return null;
}

// Look up the coach-facing designer route for a scene from the
// per-domain scene-type catalog.  The catalog row's ``designer_route``
// is the single source of truth — no hardcoded scene_type → route
// mappings live in the frontend.  Adding a new scene_type is purely a
// catalog change.  ``/coach/scene-data`` is the generic metadata
// editor used by scene types without a bespoke designer yet.
function designerPathFor(scene, domainId, sceneTypes) {
  const entry = resolveSceneTypeEntry(scene?.scene_type, domainId, sceneTypes);
  const base = entry?.designer_route || '/coach/scene-data';
  return `${base}/${scene.scene_id}?domain=${domainId}`;
}

export default function SceneLibraryPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  // Domain is required by the route (``/coach/scene-library/:domain``)
  // so there's no sensible "default" — falling back to ``football``
  // here was the source of stray football state leaking into AI
  // coaches' Content Libraries before they finished navigating.
  const domainId = domain;

  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Scene currently being shown in the inline Preview modal (with 2D/3D
  // toggle). null = modal closed. Set from the per-card "Preview" button.
  const [previewScene, setPreviewScene] = useState(null);
  // Lessons (tutorials) in this domain. The Library tooltip promises
  // "all teachable assets — scenes, plays, drills, quizzes" so we list
  // tutorials here too rather than forcing coaches over to the
  // standalone Lessons page just to preview/play.
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Active scene_type filter — 'all' or a scene_type id from the
  // per-domain catalog (e.g. 'video', 'slide_deck', 'play', …).  The
  // catalog itself is loaded into `sceneTypes` below from
  // GET /scenario-os/coach/domain/{id}/scene-types so the filter
  // adapts per domain pack.
  const [kindFilter, setKindFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  // The scene_type to instantiate when "+ New Scene → …" is picked.
  // Also driven by the per-domain catalog.
  const [createKind, setCreateKind] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createMenuRef = useRef(null);
  const [formations, setFormations] = useState([]);
  const [sceneTypes, setSceneTypes] = useState([]);
  const [coverages, setCoverages] = useState([]);
  // Lookup map coverage_id → display_name, for badges on scene cards.
  const coverageById = React.useMemo(() => {
    const m = {};
    coverages.forEach((c) => { m[c.coverage_id] = c.display_name || c.coverage_id; });
    return m;
  }, [coverages]);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { domain_id: domainId };
      if (statusFilter) params.status = statusFilter;
      if (filter) params.q = filter;
      const { data } = await sceneAPI.list(params);
      setScenes(data.scenes || []);
    } finally {
      setLoading(false);
    }
  };

  const reloadLessons = useCallback(() => {
    setLessonsLoading(true);
    coachAPI
      .listTutorials({ domain_id: domainId })
      .then(({ data }) => setLessons(data.tutorials || []))
      .catch(() => setLessons([]))
      .finally(() => setLessonsLoading(false));
  }, [domainId]);

  useEffect(() => {
    reload();
    reloadLessons();
    Promise.all([
      sceneAPI.listFormations(domainId),
      sceneAPI.listSceneTypes(domainId),
      coachAPI.listCoverages(domainId).catch(() => ({ data: { coverages: [] } })),
    ])
      .then(([fs, ts, covs]) => {
        setFormations(fs.data.formations || []);
        setSceneTypes(ts.data.scene_types || []);
        setCoverages(covs.data.coverages || []);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [domainId, statusFilter]);

  const handleDuplicate = async (id) => {
    const { data } = await sceneAPI.duplicate(id);
    navigate(designerPathFor(data, domainId, sceneTypes));
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete scene?',
      message: 'Delete this scene? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await sceneAPI.remove(id);
    toast.info('Scene deleted');
    reload();
  };

  const handlePublish = async (id) => {
    const ok = await confirm({
      title: 'Publish to learners?',
      message: 'This content will become visible to all enrolled learners. Tutorial segments will be synced with the latest content.',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    await sceneAPI.publish(id);
    toast.info('Published — learners can now access this content');
    reload();
  };

  const handleUnpublish = async (id) => {
    const ok = await confirm({
      title: 'Unpublish?',
      message: 'This content will be reverted to draft. Learners will still see the previously cached version in their lessons until the lesson is re-published.',
      confirmLabel: 'Unpublish',
      danger: true,
    });
    if (!ok) return;
    await sceneAPI.unpublish(id);
    toast.info('Content unpublished — reverted to draft');
    reload();
  };

  // Close the "+ New Scene" dropdown when clicking outside it.
  useEffect(() => {
    if (!showCreateMenu) return undefined;
    const onDocClick = (e) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCreateMenu]);

  // Filter applied client-side: 'all' shows everything, otherwise match the
  // resolved catalog id so domain-prefixed legacy types (e.g. "football_play")
  // still appear when the "Play" filter is selected.
  const visibleScenes = scenes.filter((s) => {
    if (kindFilter === 'all') return true;
    // Exact match covers the canonical case.
    if (s.scene_type === kindFilter) return true;
    // Resolved match handles legacy "domain_type" scene_type values.
    const resolved = resolveSceneTypeEntry(s.scene_type, domainId, sceneTypes);
    return resolved?.id === kindFilter;
  });

  const openCreateModal = (kind) => {
    setCreateKind(kind);
    setShowCreate(true);
    setShowCreateMenu(false);
  };

  return (
    <CoachLayout title={`Content Library — ${domainId}`}>
      <div className="scene-library">
        <div className="scene-library-toolbar">
          <input
            className="scene-search"
            placeholder="Search by name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && reload()}
          />
          <select className="scene-search" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select className="scene-search" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="all">All scene types</option>
            {sceneTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.label || t.display_name || t.id}</option>
            ))}
          </select>
          <button className="btn-ghost btn-sm" onClick={reload}>Refresh</button>
          <div style={{ flex: 1 }} />
          <div className="scene-create-menu" ref={createMenuRef} style={{ position: 'relative' }}>
            <button
              className="btn-primary"
              onClick={() => setShowCreateMenu((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showCreateMenu}
              disabled={sceneTypes.length === 0}
              title={sceneTypes.length === 0 ? 'No scene types configured for this domain' : undefined}
            >
              + New Scene ▾
            </button>
            {showCreateMenu && sceneTypes.length > 0 && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 4px)',
                  background: 'var(--coach-card-bg)',
                  border: '1px solid var(--coach-border)',
                  borderRadius: 6,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  minWidth: 220,
                  zIndex: 20,
                }}
              >
                {sceneTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="menuitem"
                    className="btn-ghost"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderRadius: 0,
                    }}
                    onClick={() => openCreateModal(t.id)}
                    data-testid={`new-scene-${t.id}`}
                    title={t.description || ''}
                  >
                    {t.label || t.display_name || t.id}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div>Loading scenes…</div>
        ) : (
          <div className="scene-grid">
            {visibleScenes.length === 0 && (
              <div className="scene-empty">
                {sceneTypes.length === 0
                  ? 'This domain has no scene types configured. Add SUPPORTED_SCENE_TYPE_IDS to the domain pack.'
                  : kindFilter === 'all'
                    ? 'No scenes yet. Click "+ New Scene" above to create the first one.'
                    : `No ${sceneTypes.find((t) => t.id === kindFilter)?.label || kindFilter} scenes yet — switch filter or click "+ New Scene".`}
              </div>
            )}
            {visibleScenes.map((s) => {
              // Use resolveSceneTypeEntry so legacy domain-prefixed types
              // (e.g. "football_play") correctly resolve to the catalog entry
              // for "play" and inherit its capabilities, label, and route.
              const typeDef = resolveSceneTypeEntry(s.scene_type, domainId, sceneTypes);
              const caps = new Set(typeDef?.capabilities || []);
              const isVideo = caps.has('youtube') || caps.has('vimeo') || caps.has('mp4_upload');
              const isSpatial = caps.has('has_actors');
              const typeLabel = typeDef?.label || typeDef?.display_name || s.scene_type;
              const interactionCount = isVideo
                ? (s.domain_metadata?.interactions?.length || 0)
                : (s.triggers?.length || 0);

              const coverageBadges = isSpatial ? [
                ...(s.domain_metadata?.coverage_id
                  ? [{ label: `🛡 ${coverageById[s.domain_metadata.coverage_id] || s.domain_metadata.coverage_id}` }]
                  : []),
                ...(s.domain_metadata?.coverage_beaters || []).slice(0, 3).map((cid) => ({
                  label: `⚔ ${coverageById[cid] || cid}`,
                })),
              ] : [];

              const sceneActions = [
                { label: 'Edit', variant: 'ghost', onClick: () => navigate(designerPathFor(s, domainId, sceneTypes)) },
                {
                  label: '👁 Preview', variant: 'ghost',
                  onClick: () => {
                    if (isSpatial) {
                      setPreviewScene(s);
                    } else {
                      const route = typeDef?.designer_route || '/coach/scene-data';
                      navigate(`${route}/${s.scene_id}?domain=${domainId}`);
                    }
                  },
                },
                ...(isSpatial ? [{
                  label: '▶ Try', variant: 'secondary',
                  onClick: () => window.open(`${APP_BASE}/session/${domainId}/${s.scene_id}?mode=simulation&from=coach`, '_blank', 'noopener,noreferrer'),
                }] : []),
                ...(s.status !== 'published' ? [{
                  label: '🚀 Publish', variant: 'primary',
                  onClick: () => handlePublish(s.scene_id),
                }] : [{
                  label: '⏸ Unpublish', variant: 'ghost',
                  onClick: () => handleUnpublish(s.scene_id),
                }]),
                { label: 'Duplicate', variant: 'ghost', onClick: () => handleDuplicate(s.scene_id) },
                ...(s.status !== 'published' ? [{ label: 'Delete', variant: 'danger', onClick: () => handleDelete(s.scene_id) }] : []),
              ];

              const countTag = isVideo
                ? `${interactionCount} interactions`
                : isSpatial
                  ? `${s.timeline?.length || 0} events`
                  : null;

              return (
                <CatalogCard
                  key={s.scene_id}
                  title={s.display_name}
                  description={s.description?.slice(0, 80)}
                  icon={s.thumbnail_url || '🎭'}
                  badges={[
                    { label: s.status, color: ({ draft: '#94a3b8', approved: '#22c55e', published: '#3b82f6', archived: '#6b7280' })[s.status] || '#94a3b8' },
                    { label: typeLabel },
                    { label: (s.access_tier || 'paid').toUpperCase(), color: s.access_tier === 'free' ? '#059669' : '#d97706' },
                    ...(s.content_license ? [{ label: s.content_license.replace(/_/g, ' '), color: '#6366f1' }] : []),
                    ...(s.pedagogy_status === 'needs_review' ? [{ label: 'PEDAGOGY ISSUES', color: '#dc2626' }] : []),
                    ...(s.pedagogy_status === 'warnings' ? [{ label: 'PEDAGOGY WARNINGS', color: '#f59e0b' }] : []),
                    ...(s.version > 1 ? [{ label: `v${s.version}`, color: '#8b5cf6' }] : []),
                    ...(countTag ? [{ label: countTag }] : []),
                    ...coverageBadges,
                  ]}
                  actions={sceneActions}
                  onClick={() => navigate(designerPathFor(s, domainId, sceneTypes))}
                />
              );
            })}
          </div>
        )}

        {/* Lessons section. Coaches reported the Library felt incomplete
            because it only listed scenes; tutorials/lessons live on a
            separate page. We show them here too so the Library lives up
            to its tooltip ("all teachable assets…"). Each card has a
            Play button that opens the standalone learner-side player,
            and an Edit button that jumps to the lesson builder. */}
        <div className="scene-library-lessons" style={{ marginTop: 32 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, color: 'var(--coach-text)' }}>
            Lessons {lessons.length > 0 && <span style={{ color: 'var(--coach-text-muted)', fontWeight: 400 }}>({lessons.length})</span>}
          </h3>
          {lessonsLoading ? (
            <div className="text-muted">Loading lessons…</div>
          ) : lessons.length === 0 ? (
            <div className="scene-empty">
              No lessons in this domain yet. Create one in <a href="/coach/tutorials">Lessons</a>.
            </div>
          ) : (
            <div className="scene-grid">
              {lessons.map((t) => (
                <CatalogCard
                  key={t.tutorial_id}
                  title={t.title || 'Untitled lesson'}
                  description={(t.description || '').slice(0, 80)}
                  icon="📝"
                  badges={[
                    { label: t.status },
                    { label: 'lesson' },
                    ...(t.difficulty ? [{ label: t.difficulty }] : []),
                    { label: `${(t.segments || []).length} segments` },
                  ]}
                  actions={[
                    {
                      label: '▶ Preview',
                      variant: 'secondary',
                      onClick: () => navigate(`/tutorials/${domainId}/play/${t.tutorial_id}?preview=true&from=coach`),
                    },
                    { label: 'Edit', variant: 'ghost', onClick: () => navigate(`/coach/tutorials/${t.tutorial_id}`) },
                  ]}
                />
              ))}
            </div>
          )}
        </div>

        {previewScene && (
          // Same overlay shell the SceneDesigner uses for its preview
          // modal, so the inline ScenePreview body gets a backdrop +
          // centered card. Without these wrappers ScenePreview rendered
          // inline at the bottom of the library list — coaches saw
          // "nothing happened" because the modal was below the fold.
          <div
            className="scene-preview-modal"
            onClick={() => setPreviewScene(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Scene preview"
          >
            <div className="scene-preview-content" onClick={(e) => e.stopPropagation()}>
              <ScenePreview
                scene={previewScene}
                onClose={() => setPreviewScene(null)}
                onTryAsLearner={() => {
                  window.open(
                    `${APP_BASE}/session/${domainId}/${previewScene.scene_id}?mode=simulation&from=coach`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              />
            </div>
          </div>
        )}

        {showCreate && (
          <CreateSceneModal
            domainId={domainId}
            formations={formations}
            sceneTypes={sceneTypes}
            kind={createKind}
            onClose={() => setShowCreate(false)}
            onCreated={(s) => navigate(designerPathFor(s, domainId, sceneTypes))}
          />
        )}
      </div>
    </CoachLayout>
  );
}

function MiniField({ scene }) {
  const W = 220, H = 90;
  const event = scene.timeline?.[0];
  return (
    <svg width={W} height={H} style={{ background: '#1f6b3a', borderRadius: 4 }}>
      {[20, 40, 50, 60, 80].map((yd) => (
        <line key={yd} x1={(yd / 100) * W} y1={0} x2={(yd / 100) * W} y2={H}
              stroke={yd === 50 ? '#fff' : '#b6e0c0'} strokeWidth={yd === 50 ? 1.5 : 0.5} opacity={0.6} />
      ))}
      {(scene.actors || []).map((a) => {
        const pos = event?.actor_states?.[a.actor_id]?.position || a.initial_state?.position || { x: 50, y: 26.65 };
        const cx = (pos.x / 100) * W;
        const cy = H - (pos.y / 53.3) * H;
        const fill = a.team === 'offense' ? '#1a73e8' : a.team === 'ball' ? '#8B4513' : '#e53935';
        return <circle key={a.actor_id} cx={cx} cy={cy} r={3} fill={fill} stroke="#fff" strokeWidth={0.8} />;
      })}
    </svg>
  );
}

function MiniVideo({ scene }) {
  const W = 220, H = 90;
  const meta = scene?.domain_metadata || {};
  const thumb = meta.thumbnail_url;
  const provider = meta.video_provider || 'mp4';
  const interactionCount = (meta.interactions || []).length;
  return (
    <div
      style={{
        width: W,
        height: H,
        background: thumb
          ? `center / cover no-repeat url("${thumb}"), #1f2530`
          : '#1f2530',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        color: '#fff',
        fontSize: 11,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
        }}
      >
        ▶
      </div>
      <div
        style={{
          position: 'absolute',
          top: 4,
          right: 6,
          background: 'rgba(0,0,0,0.55)',
          padding: '2px 6px',
          borderRadius: 3,
          fontSize: 10,
          textTransform: 'uppercase',
        }}
      >
        {provider}
      </div>
      {interactionCount > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            left: 6,
            background: 'rgba(0,0,0,0.55)',
            padding: '2px 6px',
            borderRadius: 3,
            fontSize: 10,
          }}
        >
          {interactionCount} pt{interactionCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}

// Domain-neutral card thumb used for any scene_type that isn't
// spatial (no field needed) and isn't a video (no player preview).
// Renders a soft gradient + the scene_type label so the card stays
// visually distinct from spatial cards without dragging in
// football-specific imagery.
function MiniGenericThumb({ scene, typeLabel }) {
  const W = 220, H = 90;
  const initials = (typeLabel || scene?.scene_type || 'Scene')
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      style={{
        width: W,
        height: H,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #6366f1 100%)',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700 }}>{initials}</div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {typeLabel}
      </div>
    </div>
  );
}

// Capability-flag lookup.  Each scene_type catalog row advertises its
// ``capabilities`` array (e.g. ``needs_formation``, ``has_actors``,
// ``has_timeline``) and the Create modal toggles UI sections off
// those flags — no hardcoded scene_type id lists anywhere in this
// component.
function sceneTypeNeedsFormation(typeDef) {
  return Array.isArray(typeDef?.capabilities) && typeDef.capabilities.includes('needs_formation');
}

function CreateSceneModal({ domainId, formations, sceneTypes, kind, onClose, onCreated }) {
  // ``kind`` is the scene_type id picked from the "+ New Scene" menu.
  // We look up the full catalog entry so the modal can adapt (label,
  // description blurb, formation pickers, etc.) without any
  // domain-specific branching.
  const typeDef = sceneTypes.find((t) => t.id === kind);
  const label = typeDef?.label || typeDef?.display_name || kind || 'Scene';
  const description = typeDef?.description || '';
  const needsFormation = sceneTypeNeedsFormation(typeDef);

  const [name, setName] = useState('');
  const [formationId, setFormationId] = useState('');
  const [defenseFormationId, setDefenseFormationId] = useState('');
  const [creating, setCreating] = useState(false);

  const offenseFormations = formations.filter((f) => f.team === 'offense');
  const defenseFormations = formations.filter((f) => f.team === 'defense');

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        domain_id: domainId,
        display_name: name,
        scene_type: kind,
      };
      if (needsFormation) {
        payload.formation_id = formationId || null;
        payload.defense_formation_id = defenseFormationId || null;
      }
      const { data } = await sceneAPI.create(payload);
      onCreated(data);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>Create {label}</h3>
        {description && (
          <p style={{ fontSize: 12, color: 'var(--coach-text-muted)', margin: '4px 0 12px' }}>
            {description}
          </p>
        )}
        <label>Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`New ${label.toLowerCase()}`}
          />
        </label>
        {needsFormation && (
          <>
            <label>Offense formation (optional)
              <select value={formationId} onChange={(e) => setFormationId(e.target.value)}>
                <option value="">(none)</option>
                {offenseFormations.map((f) => (
                  <option key={f.formation_id} value={f.formation_id}>{f.display_name}</option>
                ))}
              </select>
            </label>
            <label>Defense formation (optional)
              <select value={defenseFormationId} onChange={(e) => setDefenseFormationId(e.target.value)}>
                <option value="">(none)</option>
                {defenseFormations.map((f) => (
                  <option key={f.formation_id} value={f.formation_id}>{f.display_name}</option>
                ))}
              </select>
            </label>
          </>
        )}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleCreate} disabled={!name || !kind || creating}>
            {creating ? 'Creating…' : `Create ${label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
